import { useBuildUrl } from '@/composables/utils/useBuildUrl';
import { COMMON_TITLE } from '@/wabunka-src/constants/metaTag';

import type { MicroCMSImage } from 'microcms-js-sdk';

export interface Provider {
  fieldId: string;
  logoImg: MicroCMSImage;
  name: string;
  description: string;
}

interface Content {
  fieldId: string;
  richEditor?: string;
  html?: string;
  image?: MicroCMSImage;
}

interface EnExperienceId {
  fieldId: string;
  contentId: string;
}

/**
 * MicroCMS 返り値の型定義
 * NOTE: MicroCMS の以下ページで作成したフィールドと一致させておく
 * https://dev-calchez.microcms.io/apis/en-stories
 */
export interface EnStoryResponse {
  id: string; // slugとして使用
  title: string;
  interviewee: string;
  introduction: string;
  date?: string;
  mainImage: MicroCMSImage;
  thumbnailImage: MicroCMSImage;
  contents: Content[];
  facebookShareUrl?: string;
  twitterShareUrl?: string;
  enExperienceIds: EnExperienceId[];
  provider: Provider;
  isPrivate: boolean;
  metaTitle?: string;
  metaDescription?: string;
  ogImageUrl?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  revisedAt: string;
}

export type EnStoryState = EnStoryResponse;

export const createDefaultEnStoryState = (): EnStoryState => ({
  id: '',
  title: '',
  date: '',
  interviewee: '',
  introduction: '',
  mainImage: {
    url: '',
    width: 0,
    height: 0,
  },
  thumbnailImage: {
    url: '',
    width: 0,
    height: 0,
  },
  contents: [],
  enExperienceIds: [],
  provider: {
    fieldId: '',
    logoImg: {
      url: '',
      width: 0,
      height: 0,
    },
    name: '',
    description: '',
  },
  facebookShareUrl: '',
  twitterShareUrl: '',
  isPrivate: false,
  metaTitle: '',
  metaDescription: '',
  ogImageUrl: '',
  createdAt: '',
  updatedAt: '',
  publishedAt: '',
  revisedAt: '',
});

export type EnStoryViewModel = EnStoryState;

export type EnStoryListViewModel = EnStoryState[];

export const buildMetaData = (enStory: EnStoryViewModel | null, isNoIndex: boolean | null, path: string) => {
  if (!enStory) return {};

  const { buildUrlWithEndSlash } = useBuildUrl();

  const shouldIndex = !isNoIndex && !enStory.isPrivate;

  const title = `${enStory.title} | ${COMMON_TITLE}`;
  const description = enStory.metaDescription ?? formatHtmlForDescriptionTag(enStory.introduction);
  const ogImageUrl = enStory.ogImageUrl ?? enStory.thumbnailImage.url;
  // index 側は値を持たせない。ヘッダーを上書きするとモジュール既定の max-image-preview:large 等が落ちる。
  const robots = shouldIndex ? undefined : 'noindex,nofollow';

  let link = [{ rel: 'canonical' as const, href: buildUrlWithEndSlash(path) }];

  if (isNoIndex || enStory.isPrivate) {
    link = [];
  }

  return {
    title,
    description,
    ogImageUrl,
    robots,
    link: link.length > 0 ? link : undefined,
  };
};
